import { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import './table.scss';

import ViewContent from './view_content';
import ViewBar from './view_bar';
import { columnShape, metaShape, rowShape } from 'helpers/shared_types';

const Table = ({
  accessor,
  columns,
  data,
  meta,
  hasViewBar,
  fetching,
  fetchData,
  onSelect,
  onTextUpdate,
  onDatePick,
  onSingleDatePick,
  onStatusUpdate,
  onDelete,
  onLink,
  isSearchable,
}) => {
  const [selectedOptions, setSelectedOptions] = useState({});
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState();

  const getOptions = useCallback(() => {
    const filterableColumns = columns.filter((c) => c.isFilterable);

    filterableColumns.forEach((c) => {
      selectedOptions[c.accessor] = c.options
        .filter((o) => o.selected)
        .map((o) => `${o.value}`); //to make sure null is respected
    });

    setSelectedOptions(selectedOptions);
  }, [columns]);

  useEffect(() => {
    getOptions();
  }, [getOptions]);

  const fetchTableData = async (pageDiff = 0, selectedOptions = {}) => {
    const newPage =
      pageDiff === null ? 1 : meta ? meta.currentPage + pageDiff : 1;
    const totalPages = meta ? meta.totalPages : 10000;
    setPage(newPage);

    if (newPage === 0 && pageDiff === -1) return;
    if (newPage > totalPages && pageDiff === 1) return;

    getOptions();
    await fetchData(newPage, selectedOptions, search);
  };

  return (
    <section className="content-list">
      {hasViewBar && (
        <ViewBar
          columns={columns}
          meta={meta}
          selectedOptions={selectedOptions}
          fetching={fetching}
          isSearchable={isSearchable}
          onNextPage={() => fetchTableData(1, selectedOptions)}
          onPrevPage={() => fetchTableData(-1, selectedOptions)}
          onSearch={(search) => {
            getOptions();
            setSearch(search);
            fetchData(page, selectedOptions, search);
          }}
        />
      )}
      <ViewContent
        accessor={accessor}
        columns={columns}
        data={data}
        fetching={fetching}
        selectedOptions={selectedOptions}
        onSelectedOptionsChange={(options) => {
          setSelectedOptions(options);
          fetchTableData(null, options);
        }}
        onSelect={onSelect}
        onTextUpdate={onTextUpdate}
        onDatePick={onDatePick}
        onSingleDatePick={onSingleDatePick}
        onStatusUpdate={onStatusUpdate}
        onDelete={onDelete}
        onLink={onLink}
      />
    </section>
  );
};

Table.propTypes = {
  accessor: PropTypes.string,
  columns: PropTypes.arrayOf(columnShape),
  data: PropTypes.arrayOf(rowShape),
  meta: metaShape,
  hasViewBar: PropTypes.bool,
  fetching: PropTypes.bool,
  fetchData: PropTypes.func,
  onSelect: PropTypes.func,
  onTextUpdate: PropTypes.func,
  onDatePick: PropTypes.func,
  onSingleDatePick: PropTypes.func,
  onStatusUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  isSearchable: PropTypes.bool,
};

export default Table;
